/**
 * Budget Phase Card Component
 *
 * Collapsible card displaying detailed budget information for a single phase
 * Shows execution status, line items, and action buttons
 *
 * Design specs:
 * - Background: #15203C
 * - Border radius: 20px
 * - Padding: 30px
 * - Gap between sections: 20px
 * - Dividers: 1px solid gray-700
 */

import { useState, useMemo } from "react";
import { ChevronDown, Eye, Trash2 } from "lucide-react";
import { cn } from "@/lib/class-control";
import { BudgetPhase, BudgetLineItem } from "@/types/orcamentos.interface";
import { BudgetStatBadge } from "./budget-stat-badge";
import { BudgetProgressBar } from "./budget-progress-bar";

interface BudgetPhaseCardProps {
  /** Phase data to display */
  phase: BudgetPhase;
  /** View button handler */
  onView?: (phaseId: string) => void;
  /** Delete button handler */
  onDelete?: (phaseId: string) => void;
  /** Whether this phase is currently loading */
  isLoading?: boolean;
}

export function BudgetPhaseCard({
  phase,
  onView,
  onDelete,
  isLoading = false,
}: BudgetPhaseCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Flatten line items from all topics for display
  const lineItems: BudgetLineItem[] = useMemo(() => {
    if (!phase.topics) return [];
    return phase.topics.flatMap((topic) => topic.lineItems || []);
  }, [phase.topics]);

  // Format currency to Brazilian Real
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Format date to Brazilian format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  return (
    <div className="bg-[#15203C] rounded-lg p-4">
      <div className="flex flex-col gap-3">
        {/* Header with title and chevron */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#F6F6F6] flex-1">
            {phase.phaseName}
          </h3>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-white hover:text-[#7846FF] transition-all"
            aria-label={isExpanded ? "Recolher" : "Expandir"}
          >
            <ChevronDown
              size={20}
              className={cn(
                "transition-transform duration-200",
                isExpanded && "rotate-180"
              )}
            />
          </button>
        </div>

        {/* Divider */}
        <div className="h-px border-t border-gray-700" />

        {/* Stats row - always visible */}
        <div className="flex gap-8 items-start">
          <BudgetStatBadge
            icon={<span className="text-base">💰</span>}
            label="Orçamento Executado"
            value={phase.orcamentoExecutado}
          />
          <BudgetStatBadge
            icon={<span className="text-base">💰</span>}
            label="Orçamento Previsto"
            value={phase.orcamentoAprovado}
          />
        </div>

        {/* Progress bar - always visible */}
        <BudgetProgressBar percentage={phase.percentual} />

        {/* Divider */}
        <div className="h-px border-t border-gray-700" />

        {/* Expanded content - line items */}
        {isExpanded && (
          <>
            {/* Line items */}
            <div className="flex flex-col gap-1.5">
              {lineItems.map((item, index) => (
                <div
                  key={item.id || index}
                  className="flex justify-between items-center text-sm text-white"
                >
                  <p className="font-normal">{item.name}</p>
                  <p className="font-semibold whitespace-nowrap">
                    {formatCurrency(item.value)}
                  </p>
                </div>
              ))}
            </div>

            {/* Divider */}
            <div className="h-px border-t border-gray-700" />

            {/* Total row - green and bold */}
            <div className="flex justify-between items-center">
              <p className="text-sm font-bold text-white">
                Orçamento Executado
              </p>
              <p className="text-lg font-bold text-[#13D480]">
                {formatCurrency(phase.orcamentoExecutado)}
              </p>
            </div>

            {/* Divider */}
            <div className="h-px border-t border-gray-700" />
          </>
        )}

        {/* Footer - dates and action buttons */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <p className="text-xs text-[#888888]">
              Iniciado em: {formatDate(phase.iniciadoEm)}
            </p>
            <p className="text-xs text-[#888888]">
              Atualizado em: {formatDate(phase.atualizadoEm)}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1.5 bg-[#0C1427] rounded-full px-2 py-1.5">
            <button
              onClick={() => onView?.(phase.id)}
              disabled={isLoading}
              className="text-white hover:text-[#13D480] disabled:text-[#888888] disabled:cursor-not-allowed transition-colors p-1"
              aria-label="Visualizar"
              title="Visualizar"
            >
              <Eye size={16} />
            </button>
            <button
              onClick={() => onDelete?.(phase.id)}
              disabled={isLoading}
              className="text-white hover:text-[#F1745B] disabled:text-[#888888] disabled:cursor-not-allowed transition-colors p-1"
              aria-label="Deletar"
              title="Deletar"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
